import { z } from 'zod';

const text = z
  .string()
  .nullish()
  .transform((value) => value || undefined);
const player = z.object({
  participantId: z.string().min(1),
  login: z.string(),
  displayName: z.string(),
  ready: z.boolean(),
  connected: z.boolean(),
  isMaster: z.boolean(),
  screenOnly: z.boolean().default(false),
  active: z.boolean().default(true),
  teamId: text,
});
const score = z.object({
  participantId: z.string(),
  displayName: z.string(),
  place: z.number().int().positive(),
  score: z.number().nonnegative(),
  correctCount: z.number().int().nonnegative().default(0),
  teamId: text,
  teamName: text,
});
export const partySnapshotSchema = z.object({
  roomId: z.string().min(1),
  code: z.string().regex(/^\d{6}$/),
  revision: z.number().int().nonnegative(),
  serverNow: z.string(),
  phase: z.enum(['lobby', 'preparing', 'question', 'reveal', 'finished', 'closed']),
  gameId: text,
  screenOnly: z.boolean(),
  questionCount: z.number().int().min(10).max(50),
  kinds: z.array(z.enum(['film', 'series', 'game', 'anime'])).min(1),
  masterParticipantId: z.string(),
  you: player.nullish(),
  players: z.array(player),
  teams: z.array(z.object({ id: z.string(), name: z.string() })),
  question: z
    .object({
      id: z.string(),
      index: z.number().int().positive(),
      total: z.number().int().positive(),
      kind: z.string(),
      difficulty: z.number().int().min(1).max(5),
      screenshot: z.string().optional(),
      screenshots: z.array(z.string()).optional(),
      options: z.array(z.object({ titleId: z.string(), name: z.string() })).length(4),
    })
    .nullish(),
  answeredCount: z.number().int().nonnegative().optional(),
  participantCount: z.number().int().nonnegative().optional(),
  yourAnswerTitleId: text,
  correctTitleId: text,
  questionStartedAt: text,
  questionDeadlineAt: text,
  nextQuestionAt: text,
  lobbyExpiresAt: text,
  resultsExpiresAt: text,
  results: z
    .object({
      players: z
        .array(score)
        .nullish()
        .transform((value) => value ?? []),
      teams: z
        .array(
          z.object({
            teamId: z.string(),
            teamName: z.string(),
            memberIds: z.array(z.string()),
            place: z.number().int().positive(),
            score: z.number().nonnegative(),
            correctCount: z.number().int().nonnegative().default(0),
          }),
        )
        .nullish()
        .transform((value) => value ?? []),
    })
    .nullish(),
});
export type PartySnapshot = z.infer<typeof partySnapshotSchema>;
export const partyEntrySchema = z.object({
  code: z.string(),
  participantId: z.string(),
  isMaster: z.boolean(),
  snapshot: partySnapshotSchema,
});
export type PartyEntry = z.infer<typeof partyEntrySchema>;
export const partyEventSchema = z.object({
  type: z.enum([
    'state',
    'lobby_updated',
    'question',
    'reveal',
    'finished',
    'ack',
    'answer_received',
    'room_closed',
    'error',
  ]),
  snapshot: partySnapshotSchema.optional(),
  actionId: text,
  code: text,
  message: text,
  revision: z.number().int().nonnegative().optional(),
  roomId: text,
  gameId: text,
  questionId: text,
  correctTitleId: text,
  serverNow: text,
  questionStartedAt: text,
  questionDeadlineAt: text,
  nextQuestionAt: text,
  answeredCount: z.number().int().nonnegative().optional(),
  participantCount: z.number().int().nonnegative().optional(),
});
export type PartyEvent = z.infer<typeof partyEventSchema>;
export type PartyCommand = {
  type:
    | 'update_settings'
    | 'set_screen_only'
    | 'create_team'
    | 'rename_team'
    | 'delete_team'
    | 'set_team'
    | 'set_ready'
    | 'start'
    | 'answer'
    | 'advance'
    | 'return_to_lobby'
    | 'leave';
  actionId: string;
  gameId?: string;
  payload?: Record<string, unknown>;
};
